import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Loader2, Check, X, Trash2, Eye } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";
import { ReviewLinkShare } from "@/components/admin/ReviewLinkShare";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n }) => (
  <span className="inline-flex">
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={15} className={i <= n ? "text-[#F2B705] fill-[#F2B705]" : "text-white/20"} />)}
  </span>
);

export default function ReviewsManager({ token, onAuthError }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/reviews`, auth).then((r) => setReviews(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.post(`${API}/reviews/${id}/status?status=${status}`, {}, auth);
      toast.success(status === "approved" ? "Review approved — now live on your site." : "Review hidden.");
      load();
    } catch (err) {
      if (err.response?.status === 401) { onAuthError?.(); } else toast.error("Could not update review.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${API}/reviews/${id}`, auth);
      toast.success("Review deleted.");
      load();
    } catch (err) {
      if (err.response?.status === 401) { onAuthError?.(); } else toast.error("Could not delete review.");
    }
  };

  const counts = {
    pending: reviews.filter((r) => r.status === "pending").length,
    approved: reviews.filter((r) => r.status === "approved").length,
    rejected: reviews.filter((r) => r.status === "rejected").length,
  };
  const shown = reviews.filter((r) => r.status === filter);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;

  return (
    <div data-testid="admin-reviews-manager">
      <ReviewLinkShare token={token} />
      <p className="text-white/50 text-sm mb-4">Customer reviews. Nothing shows on your website until you <b className="text-white">Approve</b> it.</p>
      <div className="flex gap-2 mb-5">
        {["pending", "approved", "rejected"].map((f) => (
          <button key={f} data-testid={`reviews-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-md text-sm font-semibold capitalize transition-colors ${filter === f ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            {f} ({counts[f]})
          </button>
        ))}
      </div>

      {shown.length === 0 ? <p className="text-white/40">No {filter} reviews.</p> : (
        <div className="space-y-3">
          {shown.map((r) => (
            <div key={r.id} data-testid={`review-row-${r.id}`} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-5">
              <div className="flex flex-wrap justify-between gap-2">
                <div>
                  <span className="font-display font-bold text-lg">{r.name}</span>
                  <span className="ml-3"><Stars n={r.rating} /></span>
                </div>
                <span className="text-xs text-white/40">{fmtDateTime(r.created_at)}</span>
              </div>
              <p className="text-white/70 mt-2 whitespace-pre-wrap">{r.comment}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {r.status !== "approved" && (
                  <button data-testid={`review-approve-${r.id}`} onClick={() => setStatus(r.id, "approved")}
                    className="flex items-center gap-1.5 bg-green-500/15 text-green-400 border border-green-500/30 text-sm px-4 py-2 rounded-md hover:bg-green-500 hover:text-white transition-colors">
                    <Check size={15} /> Approve
                  </button>
                )}
                {r.status !== "rejected" && (
                  <button data-testid={`review-reject-${r.id}`} onClick={() => setStatus(r.id, "rejected")}
                    className="flex items-center gap-1.5 border border-white/15 text-sm px-4 py-2 rounded-md hover:bg-white/10 transition-colors">
                    <X size={15} /> Hide
                  </button>
                )}
                <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r.id)}
                  className="flex items-center gap-1.5 border border-red-500/30 text-red-400 text-sm px-4 py-2 rounded-md hover:bg-red-500 hover:text-white transition-colors">
                  <Trash2 size={15} /> Delete
                </button>
                {r.status === "approved" && <span className="flex items-center gap-1.5 text-green-400 text-sm px-2 py-2"><Eye size={15} /> Live on site</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
